import mustache from "mustache";
import { IConnectRender } from "../../definitions/IConnectRender";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { fx_createFincaClimaHorarioGrpcService } from "../../../modulos/clima/services/FincaClimaHorarioGrpcService";
import {
  agruparHorarioPorDia,
  climaCategoriaIcon,
  climaCategoriaLabel,
  FincaClimaDataDiarioCalculado,
} from "../../../utils/climaUtils";
import { formatDiaMesAbr, formatDiaSemanaAbr, toISODate } from "../../../utils/fechaUtils";
import { _2d } from "../../../utils/numberUtils";
import template from "./climaForecastListaTemplate.html";

mustache.parse(template);

/** Días antes de hoy a mostrar (el resto, hasta completar `TOTAL_DIAS`, son
 *  hoy + días después). */
const DIAS_ANTES = 3;
const TOTAL_DIAS = 7;

let LISTA_ID = 0;

/** Lista de 7 días de pronóstico (Forecast): 3 antes de hoy + hoy + 3
 *  después, degradando si falta historial o pronóstico (nunca fuerza los 7
 *  exactos). El diario se calcula del horario (`agruparHorarioPorDia`, el
 *  endpoint `/diario` se está retirando). Se carga una vez al conectar (no
 *  se refresca solo). */
export class ClimaForecastListaRender implements IConnectRender {
  private readonly _id: number;
  private _disposed = false;

  constructor() {
    LISTA_ID += 1;
    this._id = LISTA_ID;
  }

  render(): string {
    return `<div id="clima-forecast-lista-${this._id}"></div>`;
  }

  connect(): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    fx_createFincaClimaHorarioGrpcService(finca.id)
      .getListAsync()
      .then((horas) => {
        if (this._disposed) return;
        this._render(this._seleccionarDias(agruparHorarioPorDia(horas)));
      })
      .catch(() => {});
  }

  dispose(): void {
    this._disposed = true;
  }

  /** Los items vienen ordenados ascendente por fecha (string "yyyy-MM-dd",
   *  comparación directa — sin volver a parsear con `Date`, que interpreta
   *  fechas sin hora como UTC y correría el día en husos horarios negativos). */
  private _seleccionarDias(
    items: FincaClimaDataDiarioCalculado[],
  ): FincaClimaDataDiarioCalculado[] {
    const hoy = toISODate(new Date());
    const indexHoy = items.findIndex((item) => item.fecha === hoy);
    if (indexHoy === -1) return items.slice(0, TOTAL_DIAS);

    const desde = Math.max(0, indexHoy - DIAS_ANTES);
    return items.slice(desde, desde + TOTAL_DIAS);
  }

  private _render(items: FincaClimaDataDiarioCalculado[]): void {
    const contenedor = document.getElementById(
      `clima-forecast-lista-${this._id}`,
    );
    if (!contenedor) return;

    const hoy = toISODate(new Date());
    const dias = items.map((item) => this._toDia(item, item.fecha === hoy));
    contenedor.innerHTML = mustache.render(template, { dias });
  }

  private _toDia(item: FincaClimaDataDiarioCalculado, esHoy: boolean) {
    return {
      esHoy,
      diaAbr: formatDiaSemanaAbr(item.fecha),
      fechaCorta: formatDiaMesAbr(item.fecha),
      condicionLabel: climaCategoriaLabel(
        item.precipitationSum,
        item.precipitationProbability,
        item.totalcloudcover,
      ),
      condicionIcon: climaCategoriaIcon(
        item.precipitationSum,
        item.precipitationProbability,
        item.totalcloudcover,
      ),
      temperaturaMax: `${_2d(item.temperatureMax)} °C`,
      temperaturaMin: `${_2d(item.temperatureMin)} °C`,
      humedadMax: `${_2d(item.relativehumidityMax)} %`,
      humedadMin: `${_2d(item.relativehumidityMin)} %`,
      precipitacionMm: `${_2d(item.precipitationSum)} mm`,
      precipitacionProb: `${_2d(item.precipitationProbability)} %`,
      vientoVelocidad: `${_2d(item.windspeed)} m/s`,
      vientoDireccion: `${_2d(item.winddirection)}`,
      nubosidad: `${_2d(item.totalcloudcover)} %`,
      horasSol: `${_2d(item.sunshinetime)} h/d`,
    };
  }
}
